import {Component, inject, signal} from '@angular/core';
import {watchParam} from "@utils";
import {rxResource} from "@angular/core/rxjs-interop";
import {UserService} from "@api-services";
import {DatePipe} from "@angular/common";
import {ButtonComponent} from "@components";
import {ModalComponent} from "@easy-cms";
import {UserDto, CreateUserRequestDto} from "@api-contracts";
import {RouterLink} from "@angular/router";
import {MediaService} from '@api-services';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';

@Component({
  selector: 'wun-table',
  standalone: true,
  imports: [
    DatePipe,
    ButtonComponent,
    ModalComponent,
    RouterLink,
    ReactiveFormsModule
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss'
})
export class TableComponent {

  private readonly mediaService = inject(MediaService);
  private readonly userService = inject(UserService);
  private readonly fb = inject(FormBuilder);
  role = watchParam<'artists' | 'admins' | 'collectors' | 'gallery'>('role')

  showDeleteModal = signal(false);
  selectedUser = signal<UserDto | undefined>(undefined)

  showCreateModal = signal(false);
  createError = signal<string | undefined>(undefined);
  createSubmitting = signal(false);

  private readonly profileTypeMap: Record<string, string> = {
    artists: 'ARTIST',
    collectors: 'COLLECTOR',
    gallery: 'GALLERY',
    admins: 'ARTIST'
  };

  createForm = this.fb.group({
    firstName:   ['', Validators.required],
    lastName:    ['', Validators.required],
    email:       ['', [Validators.required, Validators.email]],
    username:    ['', Validators.required],
    password:    ['', Validators.required],
    country:     ['', Validators.required],
    city:        ['', Validators.required],
    phone:       ['', Validators.required],
    profileType: ['', Validators.required],
    status:      [CreateUserRequestDto.StatusEnum.Active, Validators.required]
  });

  users = rxResource({
    params: this.role,
    stream: (p) => {
      const role = {
        'artists': 'ARTIST',
        'admins': 'ADMIN',
        'collectors': 'COLLECTOR',
        gallery: "GALLERY"
      }
      return this.userService.usersFindAndPaginate({
        filters: JSON.stringify({roles: {role: {name: role[p.params]}}}),
        includes: JSON.stringify(['cv']),
        pageSize: 1000
      })
    }
  })

  openCreateModal() {
    this.createForm.reset({
      status: CreateUserRequestDto.StatusEnum.Active,
      profileType: this.profileTypeMap[this.role() ?? 'artists'] ?? 'ARTIST'
    });
    this.createError.set(undefined);
    this.showCreateModal.set(true);
  }

  submitCreateUser() {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.createSubmitting.set(true);
    const v = this.createForm.getRawValue();
    this.userService.usersCreate({
      createUserRequestDto: {
        firstName:    v.firstName!,
        lastName:     v.lastName!,
        email:        v.email!,
        username:     v.username!,
        password:     v.password!,
        country:      v.country!,
        city:         v.city!,
        phone:        v.phone!,
        profileType:  v.profileType as CreateUserRequestDto.ProfileTypeEnum,
        status:       v.status as CreateUserRequestDto.StatusEnum,
        topics:       '-',
        likeCount:    0,
        emailVerified: true,
        notified:     false,
        vatNumber: '-',
        businessName: '-'
      }
    }).subscribe({
      next: () => {
        this.users.reload();
        this.showCreateModal.set(false);
        this.createSubmitting.set(false);
      },
      error: (err) => {
        this.createError.set(err?.error?.message ?? 'Errore durante la creazione');
        this.createSubmitting.set(false);
      }
    });
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
    this.selectedUser.set(undefined);
  }

  deleteUser() {
    this.userService.usersDelete({
      id: this.selectedUser()?.id!
    }).subscribe(d => {
      this.users.reload()
      this.showDeleteModal.set(false);
    })
  }

  openCV(cvId: number) {
    this.mediaService.mediaGetSignedUrl({ id: cvId }).subscribe((url: any) => {
      window.open(url, '_blank');
    });
  }
}
